import React, { useEffect, useState } from 'react';
import BottomNav from '../components/BottomNav';
import { computeAggregates, Aggregates } from '../utils/progressStorage';
import { useTranslation } from 'react-i18next';
import { trackPageView } from '../utils/analytics';
import { formatNumberForLocale } from '../utils/hebrewNumbers';

// Color constants (matching AppOnboardingPage theme)
const BLUE_ACCENT = '#3B7BF4';
const GOLD_BUTTON = '#F5C842';
const TEXT_DARK = '#1A1A2E';
const TEXT_SECONDARY = '#6B7280';
const BORDER_LIGHT = '#D1D5DB';

const iconCircle = (icon: string, bg: string) => (
  <div
    className={`w-9 h-9 flex items-center justify-center rounded-full text-white text-sm font-semibold`}
    style={{ background: bg }}
  >
    {icon}
  </div>
);

const StatCard: React.FC<{
  icon: string;
  iconBg: string;
  labelEn: string;
  labelHe: string;
  value: string | number;
}> = ({ icon, iconBg, labelEn, labelHe, value }) => (
  <div
    className="flex flex-col rounded-2xl bg-white p-3 shadow-sm min-w-[110px] flex-1"
    style={{ border: `1px solid ${BORDER_LIGHT}` }}
  >
    <div className="flex items-center gap-2">
      {iconCircle(icon, iconBg)}
      <div className="text-lg font-semibold leading-tight" style={{ color: TEXT_DARK }}>
        {value}
      </div>
    </div>
    <div className="mt-2 text-[11px] leading-tight" style={{ color: TEXT_SECONDARY }}>
      {labelEn}
      <br />
      <span style={{ color: BLUE_ACCENT }}>{labelHe}</span>
    </div>
  </div>
);

interface BadgeProps {
  label: string;
  active?: boolean;
  icon: string;
}
const AchievementBadge: React.FC<BadgeProps> = ({ label, active, icon }) => (
  <div
    className="flex flex-col items-center justify-center rounded-xl w-[110px] h-[88px] text-center p-2 text-[11px] font-medium gap-1"
    style={{
      background: active ? 'white' : '#f3f4f6',
      border: active ? `1px solid ${BLUE_ACCENT}` : '1px solid transparent',
      color: active ? TEXT_DARK : '#9ca3af',
    }}
  >
    <span className={`text-2xl ${active ? '' : 'opacity-50'}`}>{icon}</span>
    <span>{label}</span>
  </div>
);

const formatDay = (iso: string) => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: 'short' }); // Mon, Tue
};

const ProgressPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [agg, setAgg] = useState<Aggregates | null>(null);

  useEffect(() => {
    trackPageView('Progress');
    setAgg(computeAggregates());
  }, []);

  const series = agg?.dailySeries || [];
  const maxY = 100;
  const chartHeight = 90;
  const chartWidth = 300; // will scale via viewBox
  const points = series
    .map(
      (p, i) =>
        `${(i / (series.length - 1)) * chartWidth},${chartHeight - (p.avg / maxY) * chartHeight}`,
    )
    .join(' ');

  return (
    <div className="flex flex-col" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
      <header
        className="px-4 pt-3 pb-4"
        style={{ background: '#FFFFFF', borderBottom: `1px solid ${BORDER_LIGHT}` }}
      >
        <h1 className="text-lg font-semibold text-center" style={{ color: TEXT_DARK }}>
          {t('progress_title')}
        </h1>
      </header>
      <main className="px-3 pt-3 space-y-4 overflow-y-auto flex-1">
        {/* Stats */}
        <div className="grid grid-cols-2 gap-2">
          <StatCard
            icon="🔢"
            iconBg={BLUE_ACCENT}
            labelEn="Total Verses"
            labelHe="פסוקים"
            value={agg?.totalVerses ?? 0}
          />
          <StatCard
            icon="📈"
            iconBg="#0d8f4d"
            labelEn="Avg Score"
            labelHe="ציון ממוצע"
            value={(agg?.averageScore ?? 0) + '%'}
          />
          <StatCard
            icon="⏱️"
            iconBg={GOLD_BUTTON}
            labelEn="Hours Learned"
            labelHe="שעות למידה"
            value={agg?.hoursLearned ?? 0}
          />
          <StatCard
            icon="🔥"
            iconBg="#d93b04"
            labelEn="Day Streak"
            labelHe="רצף ימים"
            value={agg?.dayStreak ?? 0}
          />
        </div>

        {/* Weekly Progress */}
        <section
          className="bg-white rounded-2xl p-3 shadow-sm"
          style={{ border: `1px solid ${BORDER_LIGHT}` }}
        >
          <div className="text-[11px] font-semibold mb-2" style={{ color: TEXT_DARK }}>
            {t('progress_weekly')}
          </div>
          <div className="flex justify-center">
            <svg
              viewBox={`0 0 ${chartWidth} ${chartHeight}`}
              className="w-full max-w-[340px] h-[100px]"
            >
              <rect x="0" y="0" width="100%" height="100%" fill="#f9fafb" rx="8" />
              {series.map((p, i) => (
                <g key={i}>
                  <circle
                    cx={(i / (series.length - 1)) * chartWidth}
                    cy={chartHeight - (p.avg / maxY) * chartHeight}
                    r={4}
                    fill={i === series.length - 1 ? GOLD_BUTTON : BLUE_ACCENT}
                  />
                </g>
              ))}
              {points && (
                <polyline
                  fill="none"
                  stroke={BLUE_ACCENT}
                  strokeWidth={3}
                  strokeLinejoin="round"
                  strokeLinecap="round"
                  points={points}
                />
              )}
            </svg>
          </div>
          <div className="flex justify-between text-[10px] mt-1" style={{ color: TEXT_SECONDARY }}>
            {series.map((p, i) => (
              <span key={i}>{formatDay(p.date)}</span>
            ))}
          </div>
        </section>

        {/* Achievements */}
        <section
          className="bg-white rounded-2xl p-3 shadow-sm"
          style={{ border: `1px solid ${BORDER_LIGHT}` }}
        >
          <div
            className="text-[11px] font-semibold mb-2 flex items-center gap-1"
            style={{ color: TEXT_DARK }}
          >
            🏆 {t('progress_achievements')} / הישגים
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            <AchievementBadge
              label="First Verse"
              icon="🥇"
              active={!!agg?.achievements.firstVerse}
            />
            <AchievementBadge
              label="Perfect Score"
              icon="💯"
              active={!!agg?.achievements.perfectScore}
            />
            <AchievementBadge
              label="5 Day Streak"
              icon="🎯"
              active={!!agg?.achievements.fiveDayStreak}
            />
            <AchievementBadge
              label="Torah Scholar"
              icon="📚"
              active={!!agg?.achievements.torahScholar}
            />
            <AchievementBadge
              label="10 Day Streak"
              icon="🔥"
              active={!!agg?.achievements.tenDayStreak}
            />
            <AchievementBadge label="Master" icon="👑" active={!!agg?.achievements.master} />
          </div>
        </section>

        {/* High Scores */}
        <section
          className="bg-white rounded-2xl p-3 shadow-sm"
          style={{ border: `1px solid ${BORDER_LIGHT}` }}
        >
          <div
            className="text-[11px] font-semibold mb-2 flex items-center gap-1"
            style={{ color: TEXT_DARK }}
          >
            ✅ {t('progress_high_scores')}
          </div>
          <ul className="space-y-1 max-h-[260px] overflow-y-auto pr-1">
            {(agg?.highScores || []).map((h, i) => (
              <li
                key={i}
                className="flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-2 text-[11px]"
              >
                <span className="w-6 h-6 flex items-center justify-center rounded-full bg-[#0d8f4d] text-white text-[10px] font-semibold">
                  ✓
                </span>
                <div className="flex-1">
                  <div className="font-medium" style={{ color: TEXT_DARK }}>
                    {t('verse') || 'Verse'}{' '}
                    {formatNumberForLocale(h.verseIndex + 1, i18n.language || '')}
                  </div>
                  <div className="text-[10px]" style={{ color: TEXT_SECONDARY }}>
                    {new Date(h.dateISO).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </div>
                </div>
                <div className="font-semibold text-[11px]" style={{ color: '#0d8f4d' }}>
                  {h.score}%
                </div>
              </li>
            ))}
            {(!agg || agg.highScores.length === 0) && (
              <li className="text-center text-[11px] py-4" style={{ color: TEXT_SECONDARY }}>
                {t('progress_no_high_scores')}
              </li>
            )}
          </ul>
        </section>
      </main>
      <BottomNav />
    </div>
  );
};

export default ProgressPage;
